<template>
    <EventGrid
        :events="events"
        :promotions="promotions"
        :favouriteTournaments="tournamentIds"
        :categoryConfig="categoryConfig"
        :renderDone="renderDone"
        :areEventsLoaded="areEventsLoaded"
        :filterChangeIndicator="filterChangeIndicator"
        :showSectionHeader="showSectionHeader"
        :sidebarChangedState="sidebarChangedState"
        :contentAbove="contentAbove"
        :pinnedEvents="showPinnedEvents ? pinnedEvents : []"
        :pinnedEventIds="showPinnedEvents ? pinnedEventIds : []"
        :areEventsPinnable='showPinnedEvents'

        @toggleFavouriteMarket="toggleMarket"
        @toggleFavouriteTournament="toggleTournament"
        @loadSpecial="fetchSpecialDetail"
        @eventGridRender="$emit('eventGridRender')"
        @unpinAll="onUnpinAllEvents"

        v-bind="gridProps"
        v-on="commonEventHandlers"
    />
</template>

<script>
    import {
        keyBy as _keyBy,
        isEmpty as _isEmpty,
    } from '@lodash';
    import { mapActions, mapGetters } from 'vuex';
    import EventGrid from '@app/modules/eventGrid';
    import { ExpandedEventInUrl } from '@app/enums';
    import Helpers from '@store/modules/ui/shared/betSlip/Helpers';
    import { SelectionType } from '@core/models/shared/betSlip/betSlipEnums';

    export default {
        name: 'SportOfferEventGrid',
        props: {
            events: {
                type: Array,
                required: false,
                default() {
                    return [];
                },
            },
            promotions: {
                type: Array,
                required: false,
                default() {
                    return [];
                },
            },
            isFavouritable: {
                type: Boolean,
                default: true,
            },
            categoryConfig: {
                type: Object,
                required: false,
                default: null,
            },
            showCategoryHeader: {
                type: Boolean,
                default: true,
            },
            showSectionHeader: {
                type: Boolean,
                default: true,
            },
            renderDone: {
                type: Boolean,
                required: true,
            },
            areEventsLoaded: {
                type: Boolean,
                required: true,
            },
            filterChangeIndicator: {
                type: Number,
                required: true,
            },
            sidebarChangedState: {
                type: Boolean,
                required: true,
            },
            contentAbove: {
                type: Object,
                default: null,
            },
            showPinnedEvents: {
                type: Boolean,
                default: true,
            },
            areMarketsShownOutside: {
                type: Boolean,
                default: true,
            },
            disableAutoScroll: {
                type: Boolean,
                default: false,
            },
            shouldFetchVisualisations: {
                type: Boolean,
                default: false,
            }
        },
        watch: {
            expandedEventId(newValue, oldValue) {
                this.performActionOnToggleExpandedEvent(oldValue);

                const eventId = this.expandedEventId;
                const match = this.eventsMap[eventId];

                if (match) {
                    if (match.containsFullOddList()) {
                        this.addEventFromUrlToBetSlip(match);
                    } else {
                        const unwatch = this.$watch(
                            () => this.eventsMap[eventId].containsFullOddList(),
                            function () {
                                this.addEventFromUrlToBetSlip(this.eventsMap[eventId]);
                                unwatch();
                            }
                        );
                    }
                }
            },
        },
        computed: {
            ...mapGetters('ui/user/preferences', [
                'tournamentIds',
                'marketIds',
                'pinnedEvents',
                'pinnedEventIds',
            ]),
            ...mapGetters('ui/sportOffer/betSlip', [
                'selections',
            ]),
            ...mapGetters('data/sportOffer', [
                'eventsMap',
            ]),
            // used for grouping events
            ...mapGetters('data/sportOffer', [
                'tournaments',
                'markets',
                'sports',
            ]),
            // used for state shared among all eventGrids
            ...mapGetters('ui/sportOffer', [
                'expandedEventId'
            ]),
            gridProps() {
                return {
                    isFavouritable: this.isFavouritable,
                    showCategoryHeader: this.showCategoryHeader,
                    pinnedEventIds: _keyBy(this.pinnedEventIds),
                    favouriteMarketIds: _keyBy(this.marketIds),
                    areMarketsShownOutside: this.areMarketsShownOutside,
                    disableAutoScroll: this.disableAutoScroll,
                };
            },
            commonEventHandlers() {
                return {
                    oddClick: (...args) => this.$emit('oddClick', ...args),
                    eventClick: ({ eventId, typeId }) => {
                        this.toggleExpandedEvent({ eventId, typeId });
                    },
                    pinClick: this.onTogglePin,
                    showStream: this.prepareStream,
                };
            },
        },
        methods: {
            ...mapActions('ui/user/preferences', [
                'toggleTournament',
                'toggleMarket',
                'toggleEventPin',
                'unpinAllEvents',
            ]),
            ...mapActions('ui/sportOffer/betSlip', [
                'toggleAddRemoveSelection',
            ]),
            ...mapActions('ui/sportOffer', [
                'cancelEventSubscriptions',
                'prepareStream',
                'toggleExpandedEvent',
                'toggleExpandedEventSubscription',
                // 'checkMatchHasVisualisation',
            ]),
            ...mapActions('ui/sportOffer/sports', [
                'setIsLoading',
            ]),
            ...mapActions('data/specialOffer', [
                'fetchSpecialDetail'
            ]),
            performActionOnToggleExpandedEvent(oldEventId) {
                const eventId = this.expandedEventId;

                this.toggleExpandedEventSubscription({
                    eventId,
                    oldEventId,
                });

                // if (this.shouldFetchVisualisations) {
                    // const match = this.eventsMap[eventId];
                    // if (match && match.betRadarId) {
                    //     this.checkMatchHasVisualisation({
                    //         id: match.id,
                    //         betRadarId: match.betRadarId,
                    //         sportId: match.sportId,
                    //     });
                    // }
                // }
            },
            addEventFromUrlToBetSlip(match) {
                if (!match) {
                    return;
                }

                const {
                    [ExpandedEventInUrl.odd]: oddId,
                    [ExpandedEventInUrl.sbValue]: specialBetValue = ''
                } = this.$route.query;

                if (oddId) {
                    const selection = Helpers.findSelectionByEventId(this.selections, match.id, SelectionType.sport);

                    if (selection && selection.getOddId().toString() === oddId
                        && _isEmpty(selection.getOddSpecialBetValue()) === _isEmpty(specialBetValue)) {
                        return;
                    }

                    this.toggleAddRemoveSelection({
                        eventId: match.id,
                        oddId: parseInt(oddId, 10),
                        specialBetValue,
                    });
                }
            },
            onTogglePin(eventId) {
                const typeId = this.$route.query[ExpandedEventInUrl.type];

                if (this.expandedEventId === eventId
                    && typeId.includes('pin')
                    && this.pinnedEventIds.includes(eventId)
                ) {
                    this.toggleExpandedEvent({ eventId, typeId });
                }
                this.toggleEventPin(eventId);
            },
            onUnpinAllEvents() {
                const typeId = this.$route.query[ExpandedEventInUrl.type];

                if (this.expandedEventId
                    && typeId.includes('pin')
                    && this.pinnedEventIds.includes(this.expandedEventId)
                ) {
                    this.toggleExpandedEvent({ typeId, eventId: this.expandedEventId });
                }
                this.unpinAllEvents();
            }
        },
        beforeDestroy() {
            this.cancelEventSubscriptions();
        },
        components: {
            EventGrid,
        }
    };
</script>



// WEBPACK FOOTER //
// src/app/modules/sportOffer/sharedSubmodules/SportOfferEventGrid.vue